import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import API_BASE_URL from '../../../components/apiconfig/api-config';

const HighSeasAgreementDocument = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { agreement_id } = useParams();
  const [agreement, setAgreement] = useState(location.state?.agreement);
  const [loading, setLoading] = useState(!location.state?.agreement);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAgreementData = async () => {
      if (!agreement_id) return;
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/agreement/high-seas-agreement/${agreement_id}`, {
          credentials: 'include',
        });
        const result = await response.json();
        if (result.success) {
          setAgreement(result.data);
          console.log('Agreement data loaded:', result.data);
        } else {
          throw new Error(result.message || 'Failed to load agreement');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (!location.state?.agreement && agreement_id) {
      fetchAgreementData();
    }
  }, [agreement_id, location.state?.agreement]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) return <p className="text-center">Loading agreement data...</p>;
  if (error) return <p className="text-center text-red-600">Error: {error}</p>;
  if (!agreement) return <p className="text-center">Agreement not found.</p>;

  return (
    <div className="max-w-5xl mx-auto my-10 p-10 bg-white border border-gray-300 shadow-md rounded-lg print:p-4 print:shadow-none print:border-none">
      {/* Header */}
      <div className="flex justify-between mb-6 no-print">
        <button onClick={() => navigate('/high-seas-agreement')} className="text-sm flex items-center gap-1 text-blue-600 hover:underline">
          <FaArrowLeft /> Back
        </button>
        <button onClick={() => window.print()} className="text-sm px-4 py-1 bg-blue-600 text-white rounded hover:bg-blue-700">
          Print Document
        </button>
      </div>

      {/* Title */}
      <div className="text-center mb-8">
        <p><b>Contract No:</b> {agreement.agreement_id}</p>
        <h2 className="uppercase text-xl border-b border-black pb-2 my-2 font-bold">High Seas Sale Agreement</h2>
        <p><i>Memorandum of Agreement entered into this <br />{formatDate(agreement.agreement_date)}</i></p>
      </div>

      {/* Seller Info */}
      <div className="mb-8">
        <p className="text-center font-bold">BETWEEN</p>
        <div className="mb-4 border border-gray-300 rounded p-4">
          <h3 className="font-bold text-lg mb-2">M/S. {agreement.kribhco_name}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p><b>Address:</b> {agreement.kribhco_address}</p>
              <p><b>State - Pincode:</b> {agreement.kribhco_state} - {agreement.kribhco_pincode}</p>
            </div>
            <div>
              <p><b>IEC CODE:</b> {agreement.kribhco_iec_code}</p>
              <p><b>AD CODE:</b> {agreement.kribhco_ad_code}</p>
            </div>
            <div>
              <p><b>GSTIN:</b> {agreement.kribhco_gst_number}</p>
            </div>
            <div>
              <p><b>PAN:</b> {agreement.kribhco_pan_no}</p>
            </div>
          </div>
          <p className="mt-2"><i>Hereinafter referred to as "SELLER"</i></p>
        </div>

        <p className="text-center font-bold my-4">AND</p>

        <div className="border border-gray-300 rounded p-4">
          <h3 className="font-bold text-lg mb-2">{agreement.supplier_name}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p><b>Address:</b> {agreement.supplier_address}</p>
              <p><b>State - Pincode:</b> {agreement.supplier_state} - {agreement.supplier_pincode}</p>
            </div>
            <div>
              <p><b>IEC CODE:</b> {agreement.supplier_iec_code}</p>
              <p><b>AD CODE:</b> {agreement.supplier_ad_code}</p>
            </div>
            <div>
              <p><b>GSTIN:</b> {agreement.supplier_gst}</p>
            </div>
            <div>
              <p><b>PAN:</b> {agreement.supplier_pan_no}</p>
            </div>
          </div>
          <p className="mt-2"><i>Hereinafter referred to as "BUYER"</i></p>
        </div>
      </div>

      {/* Terms */}
      <p>WHEREAS the Sellers has arranged for Import of <b>{agreement.commodity_name}</b> CROP YEAR: {agreement.crop_year}.</p>
      <p className="mb-4">WHEREAS the parties are desirous of selling the consignment on "HIGH SEAS SALE" on the following terms and conditions which is Witnesses the as under</p>

      <ol className="list-decimal pl-6 space-y-1">
        <li><b>Material:</b> {agreement.commodity_name}</li>
        <li><b>Quantity:</b> {agreement.order_quantity} MTS</li>
        <li><b>Vessel:</b> {agreement.vessel_name || 'Not Available'}</li>
        <li><b>B/L No:</b> {agreement.bl_no || 'Not Available'}, Dated: {formatDate(agreement.bl_issue_date)}</li>
        <li><b>Supplier Invoice Ref No:</b> {agreement.supplier_invoice_ref_no}</li>
        <li><b>High Seas Sale Price:</b> USD {agreement.hss_usd_pmt} PMT CFR KOLKATA, INDIA. TOTAL USD {agreement.hss_usd_pmt}</li>
        <li><b>Insurance:</b> TO BE ARRANGED BY THE SELLER</li>
        <li><b>Duty:</b> Customs Import duty and other levy duty to be paid by the buyers</li>
        <li><b>Tax:</b> Any Sales Tax Central or Sate and like any other charges if payable or imposed or leviable by any authority whatsoever either on the goods or on the prices thereof shall be paidby the BUYERS.</li>
        <li><b>Clearance:</b>The sale being on High Seas, the Buyers will make their own arrangement for obtaining delivery of the goods from Customs & Port authorities and will make the payment for Customs duty Port charges, wharfage, Handling and transport charges and for clearanceof the material at their own cost</li>
      </ol>

      {/* Payment */}
      <h3 className="mt-6 font-bold text-lg">11. Payment:</h3>
      <p>RTGS PAYMENT TO THE FOLLOWING BANK ACCOUNT 5 DAYS BEFORE ARRIVAL OF VESSEL:</p>
      <div className="pl-6">
        <p>{agreement.kribhco_name}</p>
        <p>Bank Details</p>
        <p>{agreement.kribhco_bank_name}{agreement.kribhco_bank_address}</p>
        <p>Account No: {agreement.kribhco_account_no}</p>
        <p>IFSC Code: {agreement.kribhco_ifsc_code}</p>
      </div>

      {/* Other Terms */}
      <h3 className="mt-6 font-bold text-lg">12. General Condition:</h3>
      <p>All formalities and expenses for clearance will be on Buyer's account.</p>

      <h3 className="mt-6 font-bold text-lg">13. Acceptance of Goods:</h3>
      <ol className="list-[lower-alpha] pl-8 space-y-1">
        <li>Title of the goods shall be transferred only after the High Seas Agreement is duly signed and handed over by the Seller to the Buyer, properly endorsed in the Buyer's favor.</li>
        <li>Any amendment or supplement to this agreement shall be valid only if mutually agreed upon in writing and signed by authorized representatives of both parties.</li>
        <li>In case of damage or deficiency of goods during transit or unloading, claims shall be supported by proper documentation including test/survey reports and initial claims lodged with the Port Trust and Steamship Company. Any settlement between the Seller and their foreign supplier shall also be binding on the Buyer.</li>
        <li>This sale is subject to the Force Majeure Clause.</li>
      </ol>
      <p className="italic mt-2">This contract is also subject to Government directions/regulations.</p>

      {/* Signatures */}
      <div className="flex justify-between mt-20">
        <div className="text-center">
          <p className="font-bold">For {agreement.kribhco_name}</p>
          <div className="w-52 border-b border-black my-10 mx-auto" />
          <p>Authorized Signatory</p>
        </div>
        <div className="text-center">
          <p className="font-bold">For {agreement.supplier_name}</p>
          <div className="w-52 border-b border-black my-10 mx-auto" />
          <p>Authorized Signatory</p>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center mt-10 text-sm text-gray-500">
        Generated on {new Date().toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })}
      </div>
    </div>
  );
};

export default HighSeasAgreementDocument;